import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import { MapPin, Car, MessageCircle, Phone, Landmark, ArrowRight, Sparkles, Calculator } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";
import { CLINIC, waLink, telLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { REF, AUTHOR_REF, PUBLISHER_REF, SPEAKABLE } from "../lib/schema";
import { LANDING_PAGES } from "../data/landingPages";

const Location = () => {
  const { city } = useParams();
  const { pathname } = useLocation();
  const [loc, setLoc] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [notFound, setNotFound] = useState(false);

  // Derive neighborhood vs district from the URL prefix (not the API response)
  // so that the canonical URL and page meta are correct on the first render —
  // otherwise the hook briefly writes `/locations/bhu` before the API resolves
  // and then flips to `/varanasi/bhu`, which confuses crawlers that snapshot
  // the hydrated DOM early.
  const isN = pathname.startsWith("/varanasi/");
  const kw = loc?.h1_keyword || (isN ? "Cataract Surgeon" : "Eye Specialist");
  const pageHref = isN ? `/varanasi/${city}` : `/locations/${city}`;

  usePageMeta({
    title: loc
      ? `${kw} ${isN ? "in" : "for"} ${loc.city} — Dr. Shantanu Kumar Gupta, Varanasi`
      : "Locations | Dr. Shantanu Kumar Gupta",
    path: pageHref,
    description: loc
      ? `${loc.tagline} Cataract, retina and cornea care by an MAMC (New Delhi) trained ophthalmologist — trusted by patients from ${loc.city}.`
      : undefined,
    // Thin variants (documented in CMS as `noindex: true`) are dropped from
    // the Google/Bing index but kept in-site so internal links continue to
    // pass authority to the canonical parent page. This removes doorway-page
    // risk on hyperlocal neighbourhood variants that duplicate boilerplate.
    noindex: loc?.noindex === true,
  });

  const schema = useMemo(() => {
    if (!loc) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "MedicalClinic",
          "@id": `https://drshantanugupta.com${pageHref}#business`,
          name: `${CLINIC.name} — ${kw} ${isN ? "in" : "for"} ${loc.city}`,
          description: loc.intro,
          url: `https://drshantanugupta.com${pageHref}`,
          telephone: CLINIC.phone,
          image: "https://drshantanugupta.com/og-image.jpg",
          medicalSpecialty: ["Ophthalmology", "Cataract Surgery", "Medical Retina"],
          hasMap: CLINIC.googleReviewsUrl,
          sameAs: [CLINIC.googleReviewsUrl],
          areaServed: isN
            ? {
                "@type": "Place",
                name: `${loc.city}, ${loc.parent_city || "Varanasi"}`,
                containedInPlace: {
                  "@type": "City",
                  name: loc.parent_city || "Varanasi",
                  containedInPlace: {
                    "@type": "AdministrativeArea",
                    name: "Purvanchal (eastern Uttar Pradesh)",
                    containedInPlace: { "@type": "State", name: "Uttar Pradesh", containedInPlace: { "@type": "Country", name: "India" } },
                  },
                },
              }
            : {
                "@type": "City",
                name: loc.city,
                containedInPlace: {
                  "@type": "State",
                  name: loc.state,
                  containedInPlace: { "@type": "Country", name: "India" },
                },
              },
          address: {
            "@type": "PostalAddress",
            streetAddress: "40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund",
            addressLocality: "Varanasi",
            addressRegion: "Uttar Pradesh",
            postalCode: "221005",
            addressCountry: "IN",
          },
          geo: loc.lat && loc.lng ? { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } : undefined,
          openingHours: ["Mo-Sa 10:00-20:00", "Su 10:00-14:00"],
          parentOrganization: { "@id": REF.clinic },
          employee: { "@id": REF.physician },
        },
        {
          "@type": "FAQPage",
          "@id": `https://drshantanugupta.com${pageHref}#faq`,
          speakable: SPEAKABLE,
          about: { "@id": REF.clinic },
          publisher: PUBLISHER_REF,
          author: AUTHOR_REF,
          mainEntity: (loc.faqs || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
              author: AUTHOR_REF,
              publisher: PUBLISHER_REF,
            },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: isN
            ? [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://drshantanugupta.com/" },
                { "@type": "ListItem", position: 2, name: "Varanasi", item: "https://drshantanugupta.com/locations/varanasi" },
                { "@type": "ListItem", position: 3, name: loc.city, item: `https://drshantanugupta.com${pageHref}` },
              ]
            : [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://drshantanugupta.com/" },
                { "@type": "ListItem", position: 2, name: "Locations", item: "https://drshantanugupta.com/locations/varanasi" },
                { "@type": "ListItem", position: 3, name: loc.city, item: `https://drshantanugupta.com${pageHref}` },
              ],
        },
      ],
    };
  }, [loc, isN, kw, pageHref]);
  usePageJsonLd(schema);

  useEffect(() => {
    setLoc(null);
    setNotFound(false);
    api.get(`/locations/${city}`).then((r) => setLoc(r.data)).catch(() => setNotFound(true));
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, [city]);

  if (notFound) return <Navigate to="/contact" replace />;
  if (!loc) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  const isNeighborhood = loc.page_type === "neighborhood";
  const h1Keyword = loc.h1_keyword || (isNeighborhood ? "Cataract Surgeon" : "Eye Specialist");
  const cityLocative = isNeighborhood ? `in ${loc.city}` : `for ${loc.city}`;
  const cityTestimonials = testimonials.filter((t) =>
    (t.location || "").toLowerCase().includes(loc.city.toLowerCase())
  );

  return (
    <div data-testid="location-page" data-page-type={loc.page_type || "district"}>
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <MapPin size={12} />
          {isNeighborhood ? `${loc.parent_city || "Varanasi"} · ${loc.city}` : `${loc.city} · ${loc.state}`}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          {h1Keyword} {cityLocative} — {isNeighborhood ? "Premium IOL Surgery, Retina & Dry Eye" : "Cataract, Retina & Cornea"}
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">{loc.tagline}</p>
        {loc.unique_angle && (
          <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--foreground))]/70 italic">{loc.unique_angle}</p>
        )}
      </section>

      <section className="container-page grid md:grid-cols-3 gap-6">
        <div className="border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
          <Car size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">Distance</div>
          <div className="font-serif text-xl mt-1">{loc.distance_km}</div>
        </div>
        <div className="border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
          <Landmark size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">How to reach</div>
          <div className="font-serif text-sm mt-1 leading-relaxed">{loc.travel_note}</div>
        </div>
        <div className="border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
          <MessageCircle size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">Book from {loc.city}</div>
          <div className="mt-3 flex flex-wrap gap-3">
            <a href={waLink(`Hi Dr. Shantanu, I am from ${loc.city} and would like to book an appointment.`)} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary !py-2 !px-4 text-xs" data-testid="loc-whatsapp">
              WhatsApp
            </a>
            <a href={telLink()} className="btn-ghost !py-2 !px-4 text-xs" data-testid="loc-call"><Phone size={12} /> Call</a>
          </div>
        </div>
      </section>

      <section className="py-14 container-narrow">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">About our service</div>
        <h2 className="font-serif text-3xl mt-2">Serving {loc.city} from Varanasi</h2>
        <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
        <p className="prose-elegant">{loc.intro}</p>

        {loc.landmarks?.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Nearby areas we serve</div>
            <div className="flex flex-wrap gap-2">
              {loc.landmarks.map((l) => (
                <span key={l} className="text-xs px-3 py-1.5 rounded-full border border-border">{l}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      {loc.sections?.length > 0 && (
        <section className="py-14 border-t border-border container-narrow" data-testid="loc-sections">
          <div className="space-y-10">
            {loc.sections.map((s, i) => (
              <article key={i} data-testid={`loc-section-${i}`}>
                <h2 className="font-serif text-2xl sm:text-3xl leading-tight">{s.heading}</h2>
                <div className="h-[2px] w-10 bg-[hsl(var(--accent))] mt-3 mb-4" />
                <p className="prose-elegant whitespace-pre-line">{s.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {loc.iol_note && (
        <section className="py-14 border-t border-border container-narrow" data-testid="loc-iol-selector-cta">
          <div className="rounded-3xl border border-[hsl(var(--primary))]/25 bg-gradient-to-br from-[hsl(var(--primary))]/6 via-background to-[hsl(var(--accent))]/6 p-6 md:p-10">
            <div className="overline mb-3 inline-flex items-center gap-2">
              <Sparkles size={13} /> Interactive tool · free
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight">
              Cataract lens (IOL) selection for {loc.city} patients
            </h2>
            <div className="h-[2px] w-10 bg-[hsl(var(--accent))] mt-3 mb-4" />
            <p className="prose-elegant max-w-2xl">{loc.iol_note}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/cataract-lens-selection-in-varanasi" className="btn-primary text-xs inline-flex items-center gap-2" data-testid="loc-iol-tool-cta">
                <Sparkles size={14} /> Open the IOL Selector
              </Link>
              <Link to="/tools/cataract-cost-calculator" className="btn-outline text-xs inline-flex items-center gap-2" data-testid="loc-calc-tool-cta">
                <Calculator size={14} /> Estimate surgery cost
              </Link>
              <a
                href={waLink(`Hi Dr. Shantanu, I am from ${loc.city} and used your website. I would like help choosing the right IOL lens for cataract surgery.`)}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn-outline text-xs inline-flex items-center gap-2"
                data-testid="loc-iol-whatsapp-cta"
              >
                <MessageCircle size={14} /> Ask on WhatsApp
              </a>
            </div>
            <p className="mt-4 text-[11.5px] text-muted-foreground max-w-2xl">
              The tool gives a lifestyle-matched starting point. The final lens is always
              confirmed by in-clinic biometry (IOL power measurement) — never by an online
              tool alone.
            </p>
          </div>
        </section>
      )}

      {(loc.related_services?.length > 0 || loc.nearby_locations?.length > 0) && (
        <section className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="loc-internal-links">
          <div className="container-narrow grid md:grid-cols-2 gap-10">
            {loc.related_services?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">Related services</div>
                <h2 className="font-serif text-2xl mt-2">Popular services patients from {loc.city} ask about</h2>
                <div className="h-[2px] w-10 bg-[hsl(var(--accent))] mt-3 mb-5" />
                <ul className="space-y-3">
                  {loc.related_services.map((slug) => {
                    const svc = LANDING_PAGES.find((p) => p.slug === slug);
                    if (!svc) return null;
                    return (
                      <li key={slug}>
                        <Link
                          to={`/${slug}`}
                          className="group inline-flex items-start gap-2 text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent))] transition-colors"
                          data-testid={`loc-related-service-${slug}`}
                        >
                          <ArrowRight size={14} className="mt-1.5 shrink-0 opacity-60 group-hover:translate-x-0.5 transition-transform" />
                          <span className="underline decoration-dotted underline-offset-4">{svc.h1}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {loc.nearby_locations?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">Nearby locations</div>
                <h2 className="font-serif text-2xl mt-2">We also serve patients from</h2>
                <div className="h-[2px] w-10 bg-[hsl(var(--accent))] mt-3 mb-5" />
                <ul className="space-y-3">
                  {loc.nearby_locations.map((slug) => {
                    // /varanasi/:slug for the 4 neighbourhoods, /locations/:slug otherwise.
                    const isNeighbor = ["bhu", "lanka", "sigra", "pandeypur"].includes(slug);
                    const href = isNeighbor ? `/varanasi/${slug}` : `/locations/${slug}`;
                    const label = slug.charAt(0).toUpperCase() + slug.slice(1);
                    return (
                      <li key={slug}>
                        <Link
                          to={href}
                          className="group inline-flex items-start gap-2 text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent))] transition-colors"
                          data-testid={`loc-nearby-${slug}`}
                        >
                          <MapPin size={14} className="mt-1.5 shrink-0 opacity-60" />
                          <span className="underline decoration-dotted underline-offset-4">
                            Eye {isNeighbor ? "Doctor in" : "Doctor for"} {label} patients
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {loc.faqs?.length > 0 && (
        <section className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40">
          <div className="container-narrow">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">FAQ</div>
            <h2 className="font-serif text-3xl mt-2">Common questions from {loc.city}</h2>
            <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
            <Accordion type="single" collapsible className="border-y border-border">
              {loc.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} data-testid={`loc-faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[hsl(var(--foreground))]/80">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {cityTestimonials.length > 0 && (
        <section className="py-14 container-narrow">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">Testimonials</div>
          <h2 className="font-serif text-3xl mt-2">Patients from {loc.city} we&apos;ve cared for</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
          <div className="grid md:grid-cols-2 gap-6">
            {cityTestimonials.map((t) => (
              <div key={t.name} className="border border-border rounded-2xl p-6" data-testid={`loc-test-${t.name}`}>
                <p className="prose-elegant italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  {t.name} · {t.procedure}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-14 border-t border-border">
        <div className="container-page grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">Ready when you are</div>
            <h2 className="font-serif text-3xl mt-2">Book from {loc.city} — same-day slots often available</h2>
            <p className="mt-4 prose-elegant">
              Message us on WhatsApp with your reports and we&apos;ll confirm a convenient consultation window.
              Insurance and cashless approvals are handled by our front desk.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={waLink(`Hi Dr. Shantanu, I am from ${loc.city}. I'd like to book a consultation.`)} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="loc-cta-whatsapp">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <Link to="/appointment" className="btn-ghost" data-testid="loc-cta-appointment">
                Online form <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="aspect-video rounded-2xl overflow-hidden border border-border">
            <iframe title={`Map — ${loc.city}`} src={CLINIC.mapEmbed} className="w-full h-full" loading="lazy" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
